import { motion } from "framer-motion";
import { Check, Home, Sun, Plane, MessageCircle } from "lucide-react";

const SERVICES = [
  {
    id: "interior",
    icon: Home,
    name: "Interior Photography",
    tagline: "Bright, true-to-life rooms that feel spacious",
    features: [
      "Every key room, wide and bright",
      "Flash + ambient blending for natural light",
      "Straight verticals, no fisheye look",
      "Detail shots that sell the finishes",
    ],
  },
  {
    id: "exterior",
    icon: Sun,
    name: "Exterior Photography",
    tagline: "Curb appeal that stops the scroll",
    features: [
      "Front, rear, and angle coverage",
      "Blue-sky touch-ups on grey coastal days",
      "Twilight exterior sessions available",
      "Yard, deck, and outdoor living spaces",
    ],
  },
  {
    id: "drone",
    icon: Plane,
    name: "Aerial Drone Photography",
    tagline: "Sell the location, not just the house",
    features: [
      "Bird's-eye views of the full property",
      "Lot, neighborhood & coastline context",
      "Approach and reveal angles",
      "4K aerial video clips available",
    ],
  },
];

const Services = () => {
  return (
    <section id="services" data-testid="services-section-container" className="bg-[#0E0E12] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <p className="eyebrow-label mb-4">02 · What We Shoot</p>
          <h2 className="font-display max-w-2xl text-2xl font-bold tracking-tight text-zinc-50 sm:text-3xl lg:text-4xl">
            One call covers every angle of your listing
          </h2>
          <p className="mt-4 max-w-xl text-base text-zinc-400">
            Interior, exterior, and aerial drone coverage across Astoria and the Oregon Coast —
            mixed and matched to fit each property.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.12 }}
              data-testid={`service-card-${s.id}`}
              className="luxury-card flex flex-col p-8"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
                <s.icon size={20} className="text-[#E2C275]" />
              </span>
              <h3 className="font-display mt-6 text-xl font-semibold tracking-tight text-zinc-50 sm:text-2xl">
                {s.name}
              </h3>
              <p className="mt-2 text-xs text-zinc-400">{s.tagline}</p>
              <ul className="mt-7 flex flex-1 flex-col gap-3">
                {s.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                    <Check size={15} className="mt-0.5 shrink-0 text-[#D4AF37]" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          data-testid="quote-cta-banner"
          className="mt-14 flex flex-col items-center gap-6 rounded-2xl border border-[#D4AF37]/35 bg-gradient-to-r from-[#D4AF37]/10 via-transparent to-[#D4AF37]/10 px-8 py-12 text-center sm:flex-row sm:justify-between sm:text-left"
        >
          <div>
            <h3 className="font-display text-xl font-bold tracking-tight text-zinc-50 sm:text-2xl">
              Every property is different — <span className="text-gold-gradient">so is every quote.</span>
            </h3>
            <p className="mt-2 max-w-lg text-sm text-zinc-400">
              Tell us the address and what you need, and we'll send a no-pressure quote within a few hours.
            </p>
          </div>
          <a href="#contact" data-testid="quote-cta-btn" className="btn-gold shrink-0">
            <MessageCircle size={16} /> Contact Me for a Quote
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
